import { useRef, useState } from "react";
import { Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Uploads a photo, then reports {image_id, image_caption} upward.
export const ImageCaptionField = ({ value, caption, onChange, testid = "image",
                                    label = "Photo (optional)" }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8MB or smaller");
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption || "");
    try {
      const { data } = await api.post("/images", form);
      setPreview(URL.createObjectURL(file));
      onChange({ image_id: data.id, image_caption: caption || "" });
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setPreview(null);
    onChange({ image_id: null, image_caption: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-3 border border-dashed border-slate-300 p-3">
      <div className="flex items-start gap-3">
        {preview ? (
          <div className="relative shrink-0">
            <img
              src={preview}
              alt="Preview"
              data-testid={`${testid}-preview`}
              className="h-16 w-16 border border-border object-cover"
            />
            <button
              type="button"
              data-testid={`${testid}-clear`}
              onClick={clear}
              aria-label="Remove photo"
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center border border-border bg-white text-[#FF3B30]"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-border bg-slate-50 text-slate-400">
            <ImageIcon size={22} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <Label className="overline text-slate-500">{label}</Label>
          <input
            ref={fileRef}
            data-testid={`${testid}-input`}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={pick}
            disabled={busy}
            className="mt-1.5 block w-full text-xs file:mr-3 file:border file:border-slate-300 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider"
          />
          {busy && <p className="mt-1 text-xs text-slate-500">Uploading…</p>}
        </div>
      </div>

      <div>
        <Label className="overline text-slate-500">Caption</Label>
        <Input
          data-testid={`${testid}-caption-input`}
          value={caption || ""}
          onChange={(e) => onChange({ image_id: value || null, image_caption: e.target.value })}
          placeholder="Describe what the photo shows"
          className="mt-1.5 rounded-none border-slate-300"
        />
      </div>
    </div>
  );
};
